import {
    LINE_ITEM_TYPE_CARD,
    LINE_ITEM_TYPE_CARD_TO_CARD,
} from '@/prisma/schemaConstants'
import { CUSTOM_CARD_IMAGE } from '@/src/common/constants'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import { useCartContext } from '@/src/common/contexts/CartContext'
import {
    createCartObjectFromApiResponse,
    findDeliveryByLineItemUuid,
    findLineItemByLineItemUuid,
} from '@/src/common/contexts/utilities'
import useGetCartManually from '@/src/common/hooks/useGetCartManually'
import usePostAddCart from '@/src/common/hooks/usePostAddCart'
import { recipientReducer } from '@/src/common/reducers/recipientReducer'
import addConversion from '@/src/common/utilities/addAlgoliaConversion'
import checkKYCMinMaxLimits from '@/src/common/utilities/checkKYCMinMaxLimits'
import {
    CUSTOM_IMAGE_TYPE,
    PREDESIGN_IMAGE_TYPE,
    SET_DELIVERY_TYPE,
    SET_GIFT_STATE,
    SET_RECIPIENT,
} from '@/src/modules/build-a-card/constants'
import {
    TDelivery,
    TRecipientInfo,
    TRecipientState,
} from '@/src/modules/build-a-card/inputs/types'
import { TKYCMinMaxValidations } from '@/src/modules/build-a-card/types'
import sendDataLayerEvents from '@/src/modules/build-a-card/utilities/sendDataLayerEvents'
import updateExistingVirtualOrBrandedData from '@/src/modules/build-a-card/utilities/updateExistingVirtualOrBrandedData'
import {
    recipientEmailSchema,
    recipientPhoneSchema,
} from '@/src/modules/build-a-card/validations/schemas'
import { ICart } from '@/src/modules/cart/ICart'
import { KYC_MAX_CARDS, minCards } from '@/src/modules/checkout/strings'
import {
    EMAIL_DELIVERY,
    PARTNER_CHECKOUT_PAGE,
    TEXT_DELIVERY,
} from '@/src/modules/pg-plus/constants'
import round from 'lodash/round'
import { useRouter } from 'next/router'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react'
import { QueryObserverResult } from 'react-query'
import { v4 as uuid } from 'uuid'
import { useUser } from '@/src/modules/auth/context/AuthProvider'
import { sendBasicEvent } from '@/src/common/utilities/gtag'
import determineNextRouteAndPush from '@/src/modules/build-a-card/utilities/determineNextRouteAndPush'
import {
    determineItemPrice,
    determineValueEarned,
} from '@/src/modules/partners/utilities/determinePartnerIncentiveValues'

// this context handles the virtual visa and branded pg+ purchase flows

type TGiftQuantityState = {
    setDisplayLiveErrors: Function
    isLoading: boolean
    setIsLoading: Function
    continueIsLoading: boolean
    setContinueIsLoading: Function
    validateAndAddCart: Function
    KYCMinMaxLimits: TKYCMinMaxValidations
    displayLiveErrors: boolean
    analyticWasSent: boolean
    setAnalyticWasSent: Function
    giftState: TRecipientState
    dispatch: Function
    cardQuantity: number
}

export const GiftQuantityContext = createContext<TGiftQuantityState | null>(
    null
)

export const useGiftQuantityContext = (): TGiftQuantityState => {
    const context = useContext(GiftQuantityContext)

    if (!context) {
        throw new Error('Please use GiftQuantityContext in parent component')
    }

    return context
}

interface IGiftQuantity {
    children: ReactNode
    productUuid: string
    emailDeliveryOptionUuid: string
    textDeliveryOptionUuid: string
    customerAttributionId?: number | null
}

export const GiftQuantityProvider = ({
    children,
    productUuid,
    emailDeliveryOptionUuid,
    textDeliveryOptionUuid,
    customerAttributionId,
}: IGiftQuantity) => {
    const { refetch } = useGetCartManually()
    const { mutate: addCartMutate } = usePostAddCart()
    const {
        currentStep,
        denomination,
        setDenomination,
        senderName,
        setSenderName,
        recipientMessage,
        setRecipientMessage,
        designName,
        setDesignName,
        setDesignImage,
        setPredesignUuid,
        predesignUuid,
        customDesignObject,
        setCustomDesignObject,
        setSelectedImageType,
        partnerData,
        whiteLabelIdentifiers,
    } = useBasicGiftCardContext()
    const { cartTotalLoad, cartTotalCards } = useCartContext()
    const router = useRouter()
    const { user } = useUser()

    const checkoutPath = partnerData ? PARTNER_CHECKOUT_PAGE : '/checkout'

    const [cardQuantity, setCardQuantity] = useState<number>(1)
    const [listOfRecipients, setListOfRecipients] = useState<TRecipientInfo[]>([
        {
            recipientName: '',
            recipientPhone: '',
            recipientEmail: '',
            recipientHasError: false,
            recipientErrorMessages: [],
        },
    ])

    const isBrandedGiftPage: boolean = router.route.includes('brands')
    // this is used for KYC limit tracking
    const [itemDenomFromCart, setItemDenomFromCart] = useState<number>(0)
    const [analyticWasSent, setAnalyticWasSent] = useState<boolean>(false)
    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)

    const [editingLineItemUuid, setEditingLineItemUuid] = useState<
        string | null
    >(null)
    const [editingDeliveryUuid, setEditingDeliveryUuid] = useState<
        string | null
    >(null)

    const [giftState, dispatch] = useReducer(recipientReducer, {
        isEditing: !!router?.query?.item_edit,
        listOfRecipients: listOfRecipients,
        setListOfRecipients: setListOfRecipients,
        cardQuantity: cardQuantity,
        setCardQuantity: setCardQuantity,
        deliveryType: EMAIL_DELIVERY,
        sendingViaEmail: true,
        brandName: '',
        brandCategories: [],
        brandSlug: '',
        brandImage: '',
        brandUuid: '',
        brandObjectId: '',
        brandQueryId: '',
        minValue: minCards,
        maxValue: KYC_MAX_CARDS,
        formIsValid: false,
        isPgPlus: false,
    })

    const {
        isEditing,
        sendingViaEmail,
        brandName,
        brandSlug,
        brandUuid,
        brandCategories,
        brandObjectId,
        brandQueryId,
        deliveryType,
    } = giftState

    // handles max limits (total card load, max cards)
    const KYCMinMaxLimits = checkKYCMinMaxLimits({
        isEditing: isEditing,
        totalValueLoadInCart: cartTotalLoad,
        totalNumberOfCardsInCart: cartTotalCards,
        itemDenomFromCart: itemDenomFromCart,
        newGiftTotalDenom: round(parseFloat(denomination) * cardQuantity, 2),
        newCardQuantity: cardQuantity,
    })

    // used for the add to cart buttons
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [continueIsLoading, setContinueIsLoading] = useState<boolean>(false)
    const giftType: string = brandName.trim()
        ? `${brandName} eGift`
        : 'virtual Visa'

    useEffect(() => {
        dispatch({
            type: SET_GIFT_STATE,
            payload: {
                listOfRecipients: listOfRecipients,
                cardQuantity: cardQuantity,
                isEditing: !!router?.query?.item_edit,
            },
        })
    }, [cardQuantity, listOfRecipients, router?.query?.item_edit])

    // This effect loads a LineItem for editing, if applicable.
    useEffect(() => {
        const lineItemUuidToEdit = router.query.item_edit

        // If there is no query param with the line item UUID, return.
        if (!lineItemUuidToEdit) return

        // Do a fresh fetch of the cart
        refetch().then(async (response) => {
            const cartData = { ...(response.data as ICart) }

            // Find the Delivery that contains the LineItem.
            const delivery = findDeliveryByLineItemUuid(
                cartData,
                lineItemUuidToEdit
            )

            let lineItem
            if (delivery) {
                const { delivery_details, delivery_uuid, delivery_option } =
                    delivery
                const { delivery_option_name } = delivery_option.resolved_data

                const {
                    recipient_email_address,
                    recipient_phone_number,
                    recipient_name,
                    recipient_message,
                    sender_name,
                } = delivery_details

                dispatch({
                    type: SET_DELIVERY_TYPE,
                    payload: {
                        type:
                            delivery_option_name === EMAIL_DELIVERY
                                ? EMAIL_DELIVERY
                                : TEXT_DELIVERY,
                    },
                })
                setEditingDeliveryUuid(delivery_uuid)
                setRecipientMessage(recipient_message)
                setSenderName(sender_name)

                // update the default empty recipient with the editing details
                dispatch({
                    type: SET_RECIPIENT,
                    payload: {
                        recipientName: recipient_name,
                        recipientEmail: recipient_email_address,
                        recipientPhone: recipient_phone_number,
                        recipientIndex: 0,
                    },
                })

                lineItem = findLineItemByLineItemUuid(
                    delivery,
                    lineItemUuidToEdit
                )
            }

            if (lineItem) {
                setEditingLineItemUuid(lineItem.line_item_uuid)
                setDenomination(lineItem.denomination)
                setItemDenomFromCart(
                    round(parseFloat(lineItem.denomination), 2)
                )

                if (lineItem.design?.design_uuid) {
                    setPredesignUuid(lineItem.design.design_uuid)
                    setDesignImage(
                        lineItem.design.resolved_data?.design_preview_url
                    )
                    setDesignName(lineItem.design.resolved_data?.design_name)
                    setCustomDesignObject({})
                    setSelectedImageType(PREDESIGN_IMAGE_TYPE)
                }
                if (lineItem.custom_design?.url) {
                    setCustomDesignObject(lineItem.custom_design)
                    setDesignImage(lineItem.custom_design.url)
                    setDesignName(CUSTOM_CARD_IMAGE)
                    setSelectedImageType(CUSTOM_IMAGE_TYPE)
                }
            }
        })
    }, [
        refetch,
        router.query.item_edit,
        setCustomDesignObject,
        setDenomination,
        setDesignImage,
        setDesignName,
        setPredesignUuid,
        setRecipientMessage,
        setSelectedImageType,
        setSenderName,
    ])

    useEffect(() => {
        if (currentStep === 2) {
            sendBasicEvent('select_quantity_and_delivery_step', {
                flow: 'Build A Card',
                type: giftType,
                quantity: cardQuantity,
            })

            // sends initial delivery type selection
            sendBasicEvent('select_gift_delivery', {
                flow: 'Build A Card',
                type: giftType,
                delivery_type: deliveryType,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])

    const assembleDeliveries: Function = useCallback(
        async (orderDeliveryOptionUuid: string) => {
            const newDeliveries: TDelivery[] = []
            listOfRecipients.forEach((recipient) => {
                let brandData = {}

                // if this is a branded gift, add customization data
                if (brandUuid) {
                    brandData = {
                        customization_data: {
                            selected_gyp_brand: {
                                brand_uuid: brandUuid,
                                brand_categories: brandCategories,
                            },
                        },
                    }
                }

                // whitelabel program data
                const isLoyaltyProgram = partnerData?.isLoyaltyProgram
                const brandIncentive = partnerData?.brandIncentive

                // if incentive rate provided and NOT points/miles program
                // adjust the item_price to reflect the discount
                const itemPrice =
                    brandIncentive && !isLoyaltyProgram
                        ? determineItemPrice(denomination, brandIncentive)
                        : denomination

                // add whitelabel reward data to line item in cart data
                const whiteLabelRewards = partnerData
                    ? {
                          white_label_rewards: {
                              incentive_type:
                                  partnerData?.loyaltyUnit ?? 'discount',
                              incentive_rate: partnerData?.brandIncentive ?? 0,
                              loyalty_conversion_rate:
                                  partnerData?.loyaltyConversionRate ?? 100,
                              is_loyalty_program:
                                  partnerData?.isLoyaltyProgram ?? false,
                              white_label_program_id:
                                  whiteLabelIdentifiers?.programId,
                              white_label_brand_id:
                                  whiteLabelIdentifiers?.brandId,
                              reward_value: determineValueEarned(
                                  denomination,
                                  itemPrice,
                                  isLoyaltyProgram ?? false,
                                  brandIncentive ?? 0,
                                  partnerData?.loyaltyConversionRate ?? 100
                              ),
                          },
                      }
                    : undefined

                newDeliveries.push({
                    delivery_uuid: editingDeliveryUuid ?? uuid(),
                    delivery_option: {
                        delivery_option_uuid: orderDeliveryOptionUuid,
                    },
                    delivery_details: {
                        sender_name: senderName,
                        recipient_name: recipient?.recipientName,
                        recipient_message: recipientMessage,
                        recipient_email_address: recipient?.recipientEmail,
                        recipient_phone_number: recipient?.recipientPhone,
                    },
                    line_items: [
                        {
                            line_item_uuid: editingLineItemUuid ?? uuid(),
                            line_item_type: isBrandedGiftPage
                                ? LINE_ITEM_TYPE_CARD_TO_CARD
                                : LINE_ITEM_TYPE_CARD,
                            denomination: denomination,
                            item_price: itemPrice,
                            product: {
                                product_uuid: productUuid,
                            },
                            design: {
                                design_uuid: predesignUuid,
                            },
                            custom_design: customDesignObject,
                            ...brandData,
                            ...whiteLabelRewards,
                        },
                    ],
                })
            })

            return newDeliveries
        },
        [
            brandCategories,
            brandUuid,
            customDesignObject,
            denomination,
            editingDeliveryUuid,
            editingLineItemUuid,
            isBrandedGiftPage,
            listOfRecipients,
            predesignUuid,
            productUuid,
            recipientMessage,
            senderName,
            partnerData,
        ]
    )

    const addCart = useCallback(
        async ({
            productMediumType,
            keepShopping = false,
        }: {
            productMediumType: string
            keepShopping: boolean
        }) => {
            let orderDeliveryOptionUuid: string = sendingViaEmail
                ? emailDeliveryOptionUuid
                : textDeliveryOptionUuid

            // Fetch the latest cart data from the RQ hook... then post a new cart with this new line_item.
            await refetch().then(async (response: QueryObserverResult) => {
                const cartData = createCartObjectFromApiResponse(response)
                const newDeliveries = await assembleDeliveries(
                    orderDeliveryOptionUuid
                )

                if (editingLineItemUuid) {
                    updateExistingVirtualOrBrandedData({
                        cartData: cartData,
                        newDeliveries: newDeliveries,
                        editingLineItemUuid: editingLineItemUuid,
                    })
                } else {
                    // if they're not editing, push the items into the cart
                    cartData.cart_contents.deliveries.push(...newDeliveries)

                    // if customer attribution id provided, attach to cart
                    // id will update for each new session (browser close + open)
                    if (customerAttributionId) {
                        cartData.customer_attribution_id = customerAttributionId
                    }
                }

                addCartMutate(cartData, {
                    onSuccess: async () => {
                        // if we have the brand's query and object id, sent a conversion to algolia
                        addConversion({
                            eventName: 'PG+ Added to Cart',
                            cardQuantity: cardQuantity,
                            brandObjectId: brandObjectId,
                            brandQueryId: brandQueryId,
                            userToken: user?.uid ?? 'guest',
                        })

                        // @TODO update datalayer events to reflect partner if any
                        sendDataLayerEvents({
                            isBrandedGiftPage: isBrandedGiftPage,
                            brandCategories: brandCategories,
                            brandQueryId: brandQueryId,
                            brandName: brandName,
                            cardQuantity: cardQuantity,
                            designName: designName,
                            denomination: Number(denomination),
                            productMediumType: productMediumType,
                        })

                        if (keepShopping) {
                            await determineNextRouteAndPush({
                                isEditing,
                                router,
                            })
                        } else {
                            await router.push(checkoutPath)
                        }
                    },
                    onError: () => {
                        alert('Unable to add item to cart. Please try again.')
                    },
                })
            })
        },
        [
            addCartMutate,
            assembleDeliveries,
            brandCategories,
            brandName,
            brandObjectId,
            brandQueryId,
            brandSlug,
            cardQuantity,
            denomination,
            designName,
            editingLineItemUuid,
            emailDeliveryOptionUuid,
            isBrandedGiftPage,
            isEditing,
            refetch,
            router,
            sendingViaEmail,
            textDeliveryOptionUuid,
        ]
    )

    const doValidation = async (
        sendingViaEmail: boolean,
        rec: any,
        index: number,
        newList: any
    ) => {
        newList[index].recipientErrorMessages = []
        listOfRecipients[index].recipientHasError = false
        const validation = sendingViaEmail
            ? {
                  recipientName: rec.recipientName,
                  recipientEmail: rec.recipientEmail,
                  deliveryMethod: 'validate-recipients',
              }
            : {
                  recipientName: rec.recipientName,
                  recipientPhone: rec.recipientPhone,
                  deliveryMethod: 'validate-recipients',
              }
        const recipientSchema = sendingViaEmail
            ? recipientEmailSchema
            : recipientPhoneSchema

        await recipientSchema
            .validate(validation, {
                abortEarly: false,
                context: {
                    checkPhoneBlocked: true,
                },
            })
            .then(() => {
                listOfRecipients[index].recipientHasError = false
                newList[index].recipientErrorMessages = []
                setListOfRecipients(newList)
            })
            .catch((err) => {
                newList[index].recipientHasError = true
                newList[index].recipientErrorMessages.push(err.inner[0])
            })
            .finally(() => {
                //dispatch({ type: VALIDATE_RECIPIENTS, payload: {} })
            })
    }

    async function validateAndAddCart({
        productMediumType,
        keepShopping = false,
    }: {
        productMediumType: string
        keepShopping: boolean
    }) {
        // check if all recipient info has been added to the list successfully
        const totalRecipientMatch: boolean =
            listOfRecipients.length === cardQuantity

        const newList = [...listOfRecipients]
        let index = 0
        for (const rec of listOfRecipients) {
            await doValidation(sendingViaEmail, rec, index, newList)
            index++
        }

        setListOfRecipients(newList)
        const step2FormErrors = newList.find((rec) => rec.recipientHasError)
        let hasFormErrors = false
        if (step2FormErrors !== undefined) {
            if (step2FormErrors.recipientHasError) {
                hasFormErrors = true
            }
        }

        const hasAllRecipients: boolean =
            newList.filter(
                (recipient) =>
                    recipient.recipientHasError ||
                    !recipient.recipientName ||
                    (!recipient.recipientEmail && !recipient.recipientPhone)
            ).length === 0

        if (!totalRecipientMatch || !hasAllRecipients || hasFormErrors) {
            setIsLoading(false)
            setContinueIsLoading(false)
            return
        }

        addCart({
            productMediumType: productMediumType,
            keepShopping: keepShopping,
        }).then(() => {})
    }

    const functions = useMemo(
        () => ({
            setDisplayLiveErrors,
            setIsLoading,
            setContinueIsLoading,
            setAnalyticWasSent,
        }),
        []
    )

    const values = useMemo(
        () => ({
            analyticWasSent,
            isLoading,
            continueIsLoading,
            KYCMinMaxLimits,
            displayLiveErrors,
            giftState,
            dispatch,
            validateAndAddCart,
            cardQuantity,
        }),
        [
            KYCMinMaxLimits,
            analyticWasSent,
            continueIsLoading,
            displayLiveErrors,
            giftState,
            isLoading,
            validateAndAddCart,
            cardQuantity,
        ]
    )

    const finalValues = useMemo(
        () => ({ ...values, ...functions }),
        [functions, values]
    )

    return (
        <GiftQuantityContext.Provider value={finalValues}>
            {children}
        </GiftQuantityContext.Provider>
    )
}
